import React, { FunctionComponent, useState } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';

import { PRIMARY_GREY, LIGHTEST_GREY } from 'constants/colours';

const StyledSearchBar = styled.div<{ height: number; medium: boolean }>(
  ({ height, medium }) => css`
    height: ${height}px;
    width: ${medium ? '400px' : 'auto'};
    background: ${LIGHTEST_GREY};
    display: flex;
    overflow-x: hidden;
    color: ${PRIMARY_GREY};
    order: 0;
    border-radius: 4px;
    position: relative;
    cursor: pointer;
  `
);

const SearchBarInput = styled.input<{
  $inFocus: boolean;
}>(
  ({ $inFocus }) => css`
    border: 2px solid transparent;
    border-radius: 4px;
    height: 100%;
    min-width: 250px;
    outline: none;
    vertical-align: middle;
    background: none;
    width: 100%;
    font-family: 'Poppins';
    font-size: 14px;
    color: ${PRIMARY_GREY};
    padding-right: ${$inFocus ? '3rem' : '1rem'};
    padding-left: ${$inFocus ? '1rem' : '3rem'};
    left: 0;
    right: 0;
    transition: padding 0.2s ease-out;
    &::-webkit-input-placeholder {
      transition: opacity 0.2s ease-out;
      opacity: ${$inFocus ? 0 : 1};
    }
    &::-ms-clear {
      display: none;
      width: 0;
      height: 0;
    }
    &::-ms-reveal {
      display: none;
      width: 0;
      height: 0;
    }
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }
  `
);

const InputIcon = styled(FontAwesomeIcon)`
  color: ${PRIMARY_GREY};
  position: absolute;
  bottom: 0;
  height: 100%;
  width: 1rem !important;
  transition: opacity 0.2s ease-out, margin 0.1s ease-out;
  display: flex;
  align-self: center;
`;

const SearchIcon = styled(InputIcon)<{ $inFocus: boolean }>(
  ({ $inFocus }) => css`
    pointer-events: none;
    opacity: ${$inFocus ? 0 : 1};
    left: 0;
    margin-left: ${$inFocus ? '0rem' : '1rem'};
  `
);

const CloseIcon = styled(InputIcon)<{ $inFocus: boolean; $hasValue: boolean }>(
  ({ $inFocus, $hasValue }) => css`
    opacity: ${$inFocus || $hasValue ? 0.35 : 0};
    right: 1rem;
  `
);

const SearchInput: FunctionComponent<
  React.PropsWithChildren<{
    value: string;
    setValue: (val: string) => void;
    placeholder?: string;
    height?: number;
    medium?: boolean;
  }>
> = ({ value, setValue, placeholder = 'Search', height = 40, medium = false, ...inputProps }) => {
  const [inFocus, setInFocus] = useState(false);

  return (
    <StyledSearchBar height={height} medium={medium} tabIndex={-1}>
      <SearchBarInput
        data-testid="search-input"
        role="button"
        type="search"
        value={value}
        tabIndex={0}
        placeholder={placeholder}
        onChange={(e): void => setValue(e.target.value)}
        onFocus={(): void => setInFocus(true)}
        onBlur={(): void => setInFocus(false)}
        $inFocus={inFocus}
        {...inputProps}
      />
      <SearchIcon icon={faSearch} $inFocus={inFocus} />
      <CloseIcon
        icon={faTimes}
        $inFocus={inFocus}
        $hasValue={!!value}
        onClick={(): void => setValue('')}
      />
    </StyledSearchBar>
  );
};

export default SearchInput;
